import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Wallet, AlertTriangle, ArrowRight, Loader2, RefreshCw } from "lucide-react";

/**
 * WalletPayPanel — shared "Pay via wallet" UX used by the 3
 * wallet-debit checkouts (campaign fee / listing duration /
 * featured slot). Renders one of three states:
 *
 *   ┌─ loading (skeleton-lite) ───────────────────────────────┐
 *   ┌─ enough balance ────────────────────────────────────────┐
 *   │  Wallet balance  ₹1,240.00  (Purchased ₹1k · Promo ₹240)│
 *   │  Cost           −₹588.82                                │
 *   │  Balance after   ₹651.18                                │
 *   │  [ Pay ₹588.82 from wallet ] (primary CTA)              │
 *   └─────────────────────────────────────────────────────────┘
 *   ┌─ short by ──────────────────────────────────────────────┐
 *   │  ⚠ You're short by ₹200.00                              │
 *   │  Balance  ₹388.82  ·  Needed  ₹588.82                   │
 *   │  [ Top up wallet → ] (primary CTA, opens /vendor/wallet)│
 *   │  [ Retry payment ]   (secondary, once user has topped up)│
 *   └─────────────────────────────────────────────────────────┘
 *
 * Props:
 *   loading, error, balancePaise, promoPaise, requiredPaise
 *   busy         — true while the pay request is in flight
 *   onPay        — async () => void
 *   onRefresh    — async () => void  (used after user has topped up)
 *   payLabel     — override pay button label (default "Pay ₹X from wallet")
 *   compact      — dense variant for dialogs
 *   testIdPrefix — data-testid base ("checkout-wallet", "boost-wallet", etc.)
 */

const rupees = (paise) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(Math.max(0, Number(paise || 0)) / 100);

export function WalletPayPanel({
  loading = false,
  error = null,
  balancePaise = 0,
  promoPaise = 0,
  requiredPaise = 0,
  busy = false,
  onPay,
  onRefresh,
  payLabel,
  compact = false,
  testIdPrefix = "wallet-pay",
}) {
  const balance = Number(balancePaise || 0);
  const required = Number(requiredPaise || 0);
  const promo = Number(promoPaise || 0);
  const hasEnough = balance >= required;
  const shortBy = Math.max(0, required - balance);
  const afterPaise = Math.max(0, balance - required);
  const showPromoHint = promo > 0 && hasEnough;

  const pad = compact ? "p-3" : "p-4";
  // Explicit text colors — the panel is used inside light-bg pages
  // AND inside Radix Dialog which flips text-foreground based on
  // theme. Locking the panel to a black-on-white palette avoids the
  // "white-on-white balance number" bug when embedded in the dialog.
  const textBase = "text-black";

  if (loading) {
    return (
      <div
        className={`rounded-xl border-2 border-black bg-white ${pad} animate-pulse ${textBase}`}
        data-testid={`${testIdPrefix}-loading`}
      >
        <div className="h-4 w-32 bg-black/10 rounded mb-2" />
        <div className="h-8 w-40 bg-black/10 rounded" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`rounded-xl border-2 border-black bg-white ${pad} ${textBase}`}
        data-testid={`${testIdPrefix}-error`}
      >
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          Couldn&apos;t load your wallet balance.
        </div>
        {onRefresh && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={onRefresh}
            data-testid={`${testIdPrefix}-error-retry`}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
          </Button>
        )}
      </div>
    );
  }

  // ── Enough balance ─────────────────────────────────────────────
  if (hasEnough) {
    return (
      <div
        className={`rounded-xl border-2 border-black bg-white ${pad} space-y-2.5 ${textBase}`}
        data-testid={`${testIdPrefix}-enough`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-black/70">
            <Wallet className="h-4 w-4" /> Wallet balance
          </div>
          <div className="text-right">
            <div
              className="font-heading text-lg font-semibold tabular-nums text-black"
              data-testid={`${testIdPrefix}-balance`}
            >
              {rupees(balance)}
            </div>
            {showPromoHint && (
              <div
                className="text-[10px] text-black/60 tabular-nums"
                data-testid={`${testIdPrefix}-promo-hint`}
              >
                incl. {rupees(promo)} promo · spent first
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between text-sm tabular-nums pb-1 border-b border-dashed border-black/20">
          <span className="text-black/70">This purchase</span>
          <span className="font-semibold text-black" data-testid={`${testIdPrefix}-required`}>
            − {rupees(required)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm tabular-nums">
          <span className="text-black/70">Balance after</span>
          <span
            className="font-semibold text-black"
            data-testid={`${testIdPrefix}-after`}
          >
            {rupees(afterPaise)}
          </span>
        </div>
        <Button
          type="button"
          onClick={onPay}
          disabled={busy}
          className="w-full bg-[var(--graffiti-yellow)] text-black hover:bg-[var(--graffiti-yellow)]/90 border-2 border-black font-heading text-base h-11"
          data-testid={`${testIdPrefix}-pay-btn`}
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing…
            </>
          ) : (
            <>
              <Wallet className="h-4 w-4 mr-2" />
              {payLabel || `Pay ${rupees(required)} from wallet`}
            </>
          )}
        </Button>
      </div>
    );
  }

  // ── Short by ───────────────────────────────────────────────────
  return (
    <div
      className={`rounded-xl border-2 border-red-500 bg-red-50 ${pad} space-y-2.5`}
      data-testid={`${testIdPrefix}-short`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-heading text-sm font-semibold text-red-900">
            You&apos;re short by{" "}
            <span data-testid={`${testIdPrefix}-shortfall`}>{rupees(shortBy)}</span>
          </p>
          <p className="text-xs text-red-800/80 tabular-nums mt-0.5">
            Balance <b data-testid={`${testIdPrefix}-balance`} className="text-red-900">{rupees(balance)}</b> ·
            Needed <b data-testid={`${testIdPrefix}-required`} className="text-red-900">{rupees(required)}</b>
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          asChild
          className="flex-1 bg-[var(--graffiti-yellow)] text-black hover:bg-[var(--graffiti-yellow)]/90 border-2 border-black font-heading"
          data-testid={`${testIdPrefix}-topup-btn`}
        >
          <Link to="/vendor/wallet">
            <Wallet className="h-4 w-4 mr-2" />
            Top up wallet <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
        {onRefresh && (
          <Button
            type="button"
            variant="outline"
            className="sm:w-auto border-2 border-red-500 bg-white text-red-900 hover:bg-red-100 hover:text-red-900"
            onClick={onRefresh}
            data-testid={`${testIdPrefix}-refresh-btn`}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            I&apos;ve topped up
          </Button>
        )}
      </div>
    </div>
  );
}

export default WalletPayPanel;
